import { Link } from "react-router-dom";
import { ArrowRight, Box, ChevronRight, Globe, Plane, Ship, ShieldCheck, Ticket, Truck, Clock } from "lucide-react";
import { Counter, Reveal } from "../components/motion-primitives";
import { HERO, HOME_SERVICE_CARDS, STATS, WHY_US } from "../data/content";

const SERVICE_ICONS = { plane: Plane, ship: Ship, shield: ShieldCheck, truck: Truck, box: Box, ticket: Ticket };
const WHY_ICONS = { clock: Clock, globe: Globe, shield: ShieldCheck, network: Globe };

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="home-hero">
        <div className="blob absolute -right-24 top-10 h-[420px] w-[480px] bg-blue-100/70" />
        <div className="blob absolute right-40 top-56 h-64 w-72 bg-blue-50/90" />
        <div className="blob absolute -right-10 bottom-0 h-72 w-80 bg-blue-100/50" />
        <div className="absolute right-1/3 top-24 hidden h-2 w-2 rounded-full bg-brand/70 lg:block" />
        <div className="absolute right-1/4 top-40 hidden h-1.5 w-1.5 rounded-full bg-brand/50 lg:block" />

        <span className="icon-chip absolute left-[6%] top-16 hidden animate-float lg:flex" style={{ backgroundColor: "#FEF0DC", color: "#F5941D" }}>
          <Plane className="h-5 w-5" />
        </span>
        <span className="icon-chip absolute right-[8%] top-48 hidden animate-float-slow lg:flex" style={{ backgroundColor: "#FEF0DC", color: "#F5941D" }}>
          <Ship className="h-5 w-5" />
        </span>
        <span className="icon-chip absolute bottom-24 right-[16%] hidden animate-float lg:flex" style={{ backgroundColor: "#FEF0DC", color: "#F5941D" }}>
          <Truck className="h-5 w-5" />
        </span>

        <div className="container-x relative py-24 lg:py-32">
          <Reveal>
            <span className="eyebrow-pill" data-testid="hero-badge">
              <Globe className="h-3.5 w-3.5" />
              {HERO.badge}
            </span>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-7 max-w-3xl font-heading text-5xl font-extrabold uppercase leading-[1.08] tracking-tight text-foreground md:text-7xl">
              {HERO.titleA}{" "}
              <span className="text-brand">{HERO.titleB}</span>
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-7 max-w-xl text-lg leading-relaxed text-muted-foreground">{HERO.sub}</p>
          </Reveal>
          <Reveal delay={0.24}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" data-testid="hero-cta-quote" className="btn-primary">
                Get a Quote
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/tracking" data-testid="hero-cta-track" className="btn-outline">
                Track Shipment
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-brand text-white" data-testid="stats-band">
        <div className="container-x grid grid-cols-2 gap-10 py-14 md:grid-cols-4">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.07} className="text-center">
              <Counter
                value={s.value}
                suffix={s.suffix}
                className="font-heading text-4xl font-extrabold tracking-tight md:text-5xl"
              />
              <p className="mt-2 text-xs font-medium uppercase tracking-[0.3em] text-white/90">
                {s.label}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 md:py-24" data-testid="home-services">
        <div className="container-x">
          <Reveal className="text-center">
            <span className="eyebrow-pill">What We Offer</span>
            <h2 className="mt-5 font-heading text-4xl font-extrabold tracking-tight md:text-5xl">
              Our Services
            </h2>
            <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-muted-foreground">
              Comprehensive freight solutions tailored to your business needs —
              from first mile to last mile.
            </p>
          </Reveal>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {HOME_SERVICE_CARDS.map((s, i) => {
              const Icon = SERVICE_ICONS[s.icon];
              return (
                <Reveal key={s.id} delay={(i % 3) * 0.08}>
                  <Link
                    to="/services"
                    data-testid={`service-card-${s.id}`}
                    className="group block overflow-hidden rounded-2xl border border-border bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                  >
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={s.image}
                        alt={s.title}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white" />
                      <span className="icon-chip absolute left-5 top-5">
                        <Icon className="h-5 w-5" />
                      </span>
                    </div>
                    <div className="p-6">
                      <h3 className="font-heading text-xl font-bold text-foreground">{s.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.short}</p>
                      <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand">
                        Learn More
                        <ChevronRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-section py-20 md:py-24" data-testid="home-why-us">
        <div className="container-x">
          <Reveal className="text-center">
            <span className="eyebrow-pill">Why SkyRoute</span>
            <h2 className="mt-5 font-heading text-4xl font-extrabold tracking-tight md:text-5xl">
              Why Choose Us
            </h2>
            <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-muted-foreground">
              We don't just move cargo — we build lasting partnerships built on
              trust, expertise, and results.
            </p>
          </Reveal>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {WHY_US.map((w, i) => {
              const Icon = WHY_ICONS[w.icon];
              return (
                <Reveal key={w.title} delay={i * 0.08}>
                  <div
                    data-testid={`why-card-${w.title.toLowerCase().replace(/\s/g, "-")}`}
                    className="h-full rounded-2xl border border-border bg-white p-8 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                  >
                    <span className="icon-chip-soft mx-auto" style={{ backgroundColor: "#FEF0DC" }}>
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="mt-5 font-heading text-lg font-bold">{w.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{w.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        data-testid="home-cta"
        className="bg-gradient-to-r from-[#F5941D] via-[#F9A93B] to-[#FDEBD3]"
      >
        <div className="container-x py-20 text-center md:py-24">
          <Reveal>
            <h2 className="font-heading text-4xl font-extrabold tracking-tight text-white md:text-5xl">
              Ready to Ship Globally?
            </h2>
            <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-white/90">
              Get a competitive freight quote in minutes. Our experts are
              standing by 24/7 to handle your cargo needs.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <Link to="/contact" data-testid="cta-request-quote" className="btn-white">
                Request a Quote
                <ArrowRight className="h-4 w-4" />
              </Link>
              <a href="tel:+913365983158" data-testid="cta-call-us" className="btn-ghost-white">
                Call Us Now
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
